import { z } from 'zod';
/* ------------------------------------------------------------------------- *
 * Batch lifecycle
 *
 *   received ─▶ previewing ─▶ awaiting_approval ─▶ pending ─▶ processing ─▶ processed
 *                    │                │                             └──────▶ failed
 *                    └──▶ failed      └──▶ cancelled
 *
 * Replay moves failed|processed back to pending (using the destination PAT).
 * The admin UI groups these into three buckets: pending / failed / processed.
 * ------------------------------------------------------------------------- */
export const BatchStatus = z.enum([
    'received',
    'previewing',
    'awaiting_approval',
    'pending',
    'processing',
    'processed',
    'failed',
    'cancelled',
]);
export const StatusBucket = z.enum(['pending', 'failed', 'processed', 'cancelled']);
export function bucketOf(s) {
    if (s === 'processed')
        return 'processed';
    if (s === 'failed')
        return 'failed';
    if (s === 'cancelled')
        return 'cancelled';
    return 'pending';
}
export const ItemStatus = z.enum(['pending', 'skipped', 'succeeded', 'failed']);
/** insurance = policies captured from a carrier portal or a policy file; client_data = a CRM export of people (e.g. Equisoft/connect). */
export const BatchKind = z.enum(['insurance', 'client_data']);
export const BATCH_KIND_LABEL = { insurance: 'Insurance policies', client_data: 'Client data' };
const POLICY_COLUMN = /policy|polic|contract|carrier|insurer|assureur|premium|prime|coverage|face\s*amount|sum\s*insured|beneficiar|cash\s*value|in\s*force/i;
const CLIENT_COLUMN = /name|nom|email|courriel|phone|t[ée]l[ée]phone|address|adresse|birth|naissance|dob|city|ville|postal|province|client|contact|spouse|conjoint|occupation|gender|sexe|language|langue|household|m[ée]nage|famil/i;
/**
 * The column-name check: a policy-ish header makes it an insurance import, people-ish headers alone make it
 * client data, and nothing recognisable is null (the server then asks the LLM, or falls back to client data).
 */
export function detectBatchKind(headers) {
    if (headers.some((h) => POLICY_COLUMN.test(h)))
        return 'insurance';
    if (headers.some((h) => CLIENT_COLUMN.test(h)))
        return 'client_data';
    return null;
}
/* ------------------------------------------------------------------------- *
 * Extension ⇄ server  (/api/ext/*)
 *
 * Auth: `Authorization: Bearer <Maximizer session token>` plus
 *       `X-Octopus-Base-Url: <octopus base url>` (must be on the server allowlist).
 * The server validates the token with Octopus GetSessionInfo on every call; the
 * token is held in memory only for the duration of the first write and is never
 * persisted.
 * ------------------------------------------------------------------------- */
export const CapturedItem = z.object({
    /** Portal id of the person this item is about (client / member). */
    externalId: z.string().min(1),
    /** json = structured payload from the portal's own API; html/text = DOM/PDF fallback. */
    format: z.enum(['json', 'html', 'text']),
    payload: z.unknown(),
    sourceUrl: z.string().optional(),
});
export const SubmitBatchRequest = z.object({
    /** Client-generated; resubmitting the same key returns the existing batch. */
    idempotencyKey: z.string().min(8).max(200),
    recipeId: z.string(),
    recipeVersion: z.number().int(),
    source: z.object({
        portalKey: z.string(),
        portalOrigin: z.string(),
        /** Who is signed in to the carrier portal (as shown by the portal). */
        portalUser: z.string().nullish(),
        capturedAt: z.string(),
        extensionVersion: z.string().optional(),
    }),
    items: z.array(CapturedItem).min(1).max(2000),
});
export const Identity = z.object({
    destinationId: z.string(),
    tenant: z.object({
        database: z.string(),
        displayName: z.string().nullish(),
        workspaceId: z.string().nullish(),
    }),
    user: z.object({ uid: z.string(), displayName: z.string().nullish() }),
});
export const PreviewPolicy = z.object({
    policyNumber: z.string(),
    carrierCode: z.string(),
    productType: z.string().nullish(),
    status: z.string().nullish(),
    action: z.enum(['create', 'update', 'unchanged']),
    changedFields: z.array(z.string()).optional(),
});
export const PreviewItem = z.object({
    itemId: z.string(),
    externalId: z.string(),
    clientName: z.string().nullish(),
    contact: z
        .object({
        action: z.enum(['match', 'create']),
        abEntryKey: z.string().nullish(),
        matchedBy: z.string().nullish(),
    })
        .nullish(),
    policies: z.array(PreviewPolicy),
    error: z.string().nullish(),
});
export const Preview = z.object({
    items: z.array(PreviewItem),
    totals: z.object({
        clients: z.number(),
        contactsToCreate: z.number(),
        policiesToCreate: z.number(),
        policiesToUpdate: z.number(),
        policiesUnchanged: z.number(),
        errors: z.number(),
    }),
});
export const ExtBatchView = z.object({
    batchId: z.string(),
    status: BatchStatus,
    preview: Preview.nullish(),
    error: z.string().nullish(),
    counts: z
        .object({ total: z.number(), succeeded: z.number(), failed: z.number(), skipped: z.number() })
        .nullish(),
});
export const ApproveBatchRequest = z.object({
    /** Item ids the advisor unticked in the review screen. */
    excludeItemIds: z.array(z.string()).default([]),
});
/* ------------------------------------------------------------------------- *
 * Admin panel ⇄ server  (/api/*, cookie session)
 * ------------------------------------------------------------------------- */
export const Role = z.enum(['admin', 'member']);
export const SetupRequest = z.object({
    email: z.string().email(),
    name: z.string().min(1),
    password: z.string().min(12),
});
export const LoginRequest = z.object({ email: z.string().email(), password: z.string().min(1) });
export const AcceptInviteRequest = z.object({
    token: z.string().min(16),
    name: z.string().min(1),
    password: z.string().min(12),
});
export const CreateInviteRequest = z.object({ email: z.string().email(), role: Role.default('member') });
export const UpdateUserRequest = z.object({ role: Role.optional(), disabled: z.boolean().optional() });
export const UpdateDestinationRequest = z.object({
    label: z.string().max(200).nullable().optional(),
    tenantId: z.string().max(200).nullable().optional(),
    /** string = set/replace, null = clear, undefined = leave as is. */
    pat: z.string().min(10).nullable().optional(),
});
/**
 * "Import file" on the Batches page: a PAT and CSV files read in the browser and sent as text. The PAT names the
 * tenant when Maximizer knows it (GetSessionInfo); otherwise it is taken as typed and the batch goes to the tenant
 * batches last came in for. The kind is read off the column names by the LLM, with the plain column check as fallback.
 */
export const ImportFilesRequest = z.object({
    pat: z.string().min(1),
    /** Octopus base URL the PAT belongs to; the production one when omitted. Must be on the server allowlist. */
    octopusBaseUrl: z.string().url().optional(),
    /** auto = decide from the column names. */
    kind: BatchKind.or(z.literal('auto')).default('auto'),
    files: z.array(z.object({ name: z.string().min(1).max(300), content: z.string().max(20_000_000) })).min(1).max(50),
});
/** anthropic = direct API with a stored key; maximizer = the Maximizer AI proxy, called with the advisor's own session token. */
export const LlmProvider = z.enum(['anthropic', 'maximizer']);
export const UpdateLlmSettingsRequest = z.object({
    enabled: z.boolean(),
    provider: LlmProvider,
    model: z.string().min(1).max(100),
    /** string = set/replace, null = clear, undefined = keep. */
    apiKey: z.string().min(10).nullable().optional(),
    /** Maximizer AI proxy base URL; null clears it, undefined keeps it. */
    baseUrl: z.string().url().max(300).nullable().optional(),
});
export const UpdateBlobSettingsRequest = z.object({
    container: z.string().min(1).max(100),
    prefix: z.string().max(200),
    workerContainer: z.string().min(1).max(100),
    /** string = set/replace, null = clear (fall back to the server environment), undefined = keep. */
    connectionString: z.string().min(20).nullable().optional(),
});
export const UpdateCosmosSettingsRequest = z.object({
    database: z.string().min(1).max(100),
    container: z.string().min(1).max(100),
    /** string = set/replace, null = clear (fall back to the server environment), undefined = keep. */
    connectionString: z.string().min(20).nullable().optional(),
});
/** Period for `/api/analytics`: hourly points for 24h, daily points for the rest. */
export const AnalyticsRange = z.enum(['24h', '7d', '30d', '90d']);
//# sourceMappingURL=api.js.map